import { STAGES, type Stage } from '@/papermill/stages';

import styles from './JobProgress.module.css';

export interface JobProgressProps {
  /** Index into `stages`; -1 while queued. */
  current: number;
  /** The pipeline to draw; Papermill's document stages unless told otherwise. */
  stages?: Stage[];
  title?: string;
  queuedTitle?: string;
  lede?: string;
  /** Offers a way out of a job that never finishes. */
  onDelete?: () => void;
}

/** A job's stages as a live checklist. */
export function JobProgress({
  current,
  stages = STAGES,
  title = 'Reading your document',
  queuedTitle = 'Waiting for Papermill…',
  lede = 'Updates by itself · usually under a minute',
  onDelete,
}: JobProgressProps) {
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.orb} aria-hidden="true" />
        <div>
          <p className={styles.title}>{current < 0 ? queuedTitle : title}</p>
          <p className={styles.lede}>{lede}</p>
        </div>
      </div>
      <ol className={styles.steps}>
        {stages.map((stage, index) => {
          const state = index < current ? 'done' : index === current ? 'active' : 'todo';
          return (
            <li
              key={stage.id}
              className={styles[state]}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span className={styles.dot} aria-hidden="true">
                {state === 'done' ? '✓' : index + 1}
              </span>
              {stage.label}
            </li>
          );
        })}
      </ol>
      {onDelete ? (
        <p className={styles.stuck}>
          Stuck? A job that never finishes blocks the next one.{' '}
          <button type="button" className={styles.link} onClick={onDelete}>
            Delete this job
          </button>{' '}
          to free the slot.
        </p>
      ) : null}
    </div>
  );
}
